import { computed, toValue, type ComputedRef, type Ref } from 'vue';
import { useDebounce } from '@nuxt-common/media-card-large/app/composables/useDebounce';
import { useAvailabilityBigTicket } from './useAvailabilityBigTicket';
import { useProductPrice } from '~/modules/price/composables/useProductPrice';
import { getFormattedPrice } from '~/modules/price/utils/getFormattedPrice';
import { useQuantity } from '~/modules/product/composables/useQuantity';
import useUpc from '~/modules/upc/composables/useUpc';
import { AVAILABILITY_BT_CONSTS } from '../consts/btConsts';

const WDG_SYMBOL = 'WDG_PDP'; // wdg -> white glove delivery
const DELIVERY_METHODS_PATH = '/xapi/digital/v1/product/bigticket/deliveryMethods';
const CACHE_EXPIRATION_MS = 900000; // 15 mins

export type BigTicketDeliveryMethod = {
  type: string;
  description: string;
  abbreviation: string;
  totalDeliveryFee: number;
  totalPriceWithDeliveryFee: number;
  detailDescription?: string;
  removePackingMaterial?: string;
  isSignatureRequired?: string;
  fees?: Array<{
    type: string;
    description: string;
    amount: number;
  }>;
};

export type CachedDeliveryMethods = {
  value: BigTicketDeliveryMethod[];
  expiration: number;
};

type BigTicketDeliveryMethodResponse = {
  deliveryMethods?: BigTicketDeliveryMethod[];
};

const parseDeliveryMethods = (result: BigTicketDeliveryMethodResponse): BigTicketDeliveryMethod[] => {
  return (result?.deliveryMethods ?? []).filter(({ type }) => AVAILABILITY_BT_CONSTS.SUPPORTED_DELIVERY_TYPES.has(type));
};

/**
 * Handles throttled/debounced API fetching and maintains a 15-minute client-side TTL caching layer mapped directly to query parameters.
 */
export const useBigTicketDeliveryMethodsFetch = (
  isBigTicketDeliveryActive: Ref<boolean> | ComputedRef<boolean>,
): {
  deliveryMethods: ComputedRef<BigTicketDeliveryMethod[]>;
  pending: Ref<boolean>;
} => {
  const { zipCode } = useAvailabilityBigTicket();
  const { selectedUpcNumber } = useUpc();
  const { quantity } = useQuantity();
  const selectedPrice = useProductPrice();

  // Computes the standardized query string parameters required for the Big Ticket API call
  const queryParamStr = computed(() => {
    // doesn't return price w/ dollar sign
    const formattedSelectedPrice = selectedPrice.value ? getFormattedPrice(selectedPrice.value) : '';
    if (!isBigTicketDeliveryActive.value || !selectedUpcNumber.value || !zipCode.value || !formattedSelectedPrice || Number(quantity.value) < 1) {
      return '';
    }
    return new URLSearchParams({
      upcNumber: selectedUpcNumber.value,
      selectedPrice: formattedSelectedPrice,
      quantity: String(quantity.value),
      zipCode: zipCode.value,
    }).toString();
  });

  // Debounces the API fetching logic by 300ms to throttle network requests.
  // Receives resolve/reject handlers to cleanly settle the outer Promise once the timeout fires.
  const debouncedFetch = useDebounce(
    (resolve: (value: BigTicketDeliveryMethodResponse) => void, reject: (reason?: unknown) => void, queryParams: string) => {
      if (!queryParams) {
        return resolve({ deliveryMethods: [] });
      }

      $fetch<BigTicketDeliveryMethodResponse>(`${DELIVERY_METHODS_PATH}?${queryParams}`)
        .then((response) => resolve(response))
        .catch((err) => reject(err));
    },
    300,
  );

  const { data, pending } = useAsyncData<BigTicketDeliveryMethodResponse, unknown, CachedDeliveryMethods>(
    () => `${WDG_SYMBOL}-${queryParamStr.value}`,
    // Returns a manually created pending Promise so useAsyncData instantly puts isDeliveryUpdatePending (pending)
    // to true, displaying skeleton loaders immediately while waiting for the 300ms debounce to settle.
    () => {
      const queryParams = queryParamStr.value;
      return new Promise<BigTicketDeliveryMethodResponse>((resolve, reject) => {
        debouncedFetch(resolve, reject, queryParams);
      });
    },
    {
      watch: [queryParamStr],
      immediate: !!queryParamStr.value, // only if queryParamStr is truthy,  fetch immediately
      server: false,
      deep: false,
      lazy: true,
      default: () => ({ value: [] as BigTicketDeliveryMethod[], expiration: 0 }),
      // Wraps the payload with an expiration timestamp to enable stateless TTL caching
      transform: (data) => {
        const parsedData = parseDeliveryMethods(data);

        return {
          value: parsedData,
          expiration: Date.now() + CACHE_EXPIRATION_MS,
        };
      },
      // Inspects Nuxt's payload data and enforces cache TTL checks
      getCachedData(key, nuxtApp) {
        const payload = nuxtApp.payload.data?.[key] as CachedDeliveryMethods | undefined;
        if (!payload) return undefined; // Forces fetch call on cache miss

        const isExpired = Date.now() > payload.expiration;
        if (isExpired) {
          return undefined; // Forces fetch call on expiration - overwrites existing cached value
        }

        // Return the whole wrapped payload to prevent Nuxt
        // from overwriting/corrupting the cached payload with a plain array on cache hits.
        return payload;
      },
    },
  );

  // Safely unwraps and defaults the delivery methods to a plain array to prevent template TypeError crashes.
  const deliveryMethods = computed<BigTicketDeliveryMethod[]>(() => toValue(data)?.value ?? []);

  return {
    deliveryMethods,
    pending,
  };
};
